import { createI18n } from 'vue-i18n';

import type { Locale } from './types';

import de from '@/i18n/locales/de.json';
import en from '@/i18n/locales/en.json';
import fr from '@/i18n/locales/fr.json';
import es from '@/i18n/locales/es.json';

export const storageKey = 'ts_portal_language';

export const supportedLocales: Locale[] = [
    { label: 'Deutsch', code: 'de', messages: de },
    { label: 'English', code: 'en', messages: en },
    { label: 'Français', code: 'fr', messages: fr },
    { label: 'Español', code: 'es', messages: es },
];

const fallbackLocale = 'en';

/**
 * Whether a locale code is one the UI ships.
 *
 * @param {string} locale - Locale code to test (e.g. 'de', 'en').
 * @returns {boolean} True when the code is in the supported-locale directory.
 */
export const isLocaleSupported = (locale: string): boolean => {
    return supportedLocales.some(_locale => _locale.code === locale);
}

/**
 * Resolve the initial locale: a supported stored preference, else a supported
 * browser language, else the fallback locale.
 *
 * @returns {string} The locale code to start the app in.
 */
const getInitialLocale = (): string => {
    const stored = localStorage.getItem(storageKey);

    if(stored && isLocaleSupported(stored)) {
        return stored;
    }

    const browserLang = navigator.language.split('-')[0]?.toLowerCase();

    if(browserLang && isLocaleSupported(browserLang)) {
        return browserLang;
    }

    return fallbackLocale;
}

const messages = Object.fromEntries(
    supportedLocales.map(locale => [locale.code, locale.messages])
);

const i18n = createI18n({
    legacy: false,
    locale: getInitialLocale(),
    fallbackLocale: fallbackLocale,
    messages: messages,
    warnHtmlMessage: false,
});

/**
 * Keep `<html lang>` in step with the active locale, so browser translation
 * and assistive technology read the UI in the language it is shown in. It
 * watches the global locale ref rather than hooking `setLocale` because only
 * the ref sees every source of a change: the initial locale, `setLocale` and
 * direct `composer.locale` writes.
 *
 * @param {WritableComputedRef<string>} locale - The global i18n locale ref.
 * @returns {void}
 */
const syncDocumentLanguage = (locale: WritableComputedRef<string>): void => {
    watch(locale, (code: string): void => {
        document.documentElement.lang = code;
    }, { immediate: true });
};

syncDocumentLanguage(i18n.global.locale);

export type { Locale, ExtendedComposer } from './types';

export default i18n;
